import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Card, CardContent, Badge } from "@learn-me/ui";
import { adminApi } from "@/features/admin/api/adminApi";
import {
  Plus,
  Edit,
  Trash2,
  Users,
  Eye,
  EyeOff,
  GripVertical,
} from "lucide-react";
import toast from "react-hot-toast";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { handleApiError } from "@/lib/errorHandler";
import { TeamMemberModal } from "@/features/admin/components/TeamMemberModal";

const ROLE_LABELS: Record<string, string> = {
  ACADEMIC_DIRECTOR: "Director Académico",
  EXECUTIVE_DIRECTOR: "Director Ejecutivo",
  INSTRUCTOR: "Instructor",
  OTHER: "Otro",
};

const ROLE_COLORS: Record<
  string,
  "default" | "success" | "warning" | "destructive"
> = {
  ACADEMIC_DIRECTOR: "success",
  EXECUTIVE_DIRECTOR: "success",
  INSTRUCTOR: "default",
  OTHER: "default",
};

type TeamMember = {
  id: string;
  name: string;
  role: string;
  title: string;
  photoUrl: string | null;
  bio: string;
  linkedInUrl: string | null;
  signatureUrl: string | null;
  position: number;
  isActive: boolean;
  createdAt: string;
};

export default function TeamMembersPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    member: TeamMember | null;
  }>({ isOpen: false, member: null });
  const queryClient = useQueryClient();

  const { data: members, isLoading } = useQuery({
    queryKey: ["admin-team-members"],
    queryFn: () => adminApi.getTeamMembers(true),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteTeamMember(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-team-members"] });
      toast.success("Miembro eliminado exitosamente");
      setDeleteDialog({ isOpen: false, member: null });
    },
    onError: (error) => {
      handleApiError(error, "Error al eliminar el miembro");
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (id: string) => adminApi.toggleTeamMemberActive(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["admin-team-members"] });
      toast.success(data.isActive ? "Miembro activado" : "Miembro desactivado");
    },
    onError: (error) => {
      handleApiError(error, "Error al cambiar estado del miembro");
    },
  });

  const handleEdit = (member: TeamMember) => {
    setEditingMember(member);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingMember(null);
  };

  const handleConfirmDelete = () => {
    if (deleteDialog.member) {
      deleteMutation.mutate(deleteDialog.member.id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Equipo</h1>
          <p className="text-muted-foreground">
            Gestiona los miembros del equipo institucional.
          </p>
        </div>
        <Button onClick={() => setIsModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Nuevo Miembro
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4 flex gap-4">
                <div className="w-16 h-16 bg-muted rounded-full" />
                <div className="flex-1 space-y-2">
                  <div className="h-5 bg-muted rounded w-1/3" />
                  <div className="h-4 bg-muted rounded w-1/4" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : !members || members.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">
              No hay miembros del equipo
            </h2>
            <p className="text-muted-foreground">
              Agrega a los directivos e instructores de tu equipo.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {members.map((member) => (
            <Card
              key={member.id}
              className={!member.isActive ? "opacity-60" : ""}
            >
              <CardContent className="p-4 flex items-center gap-4">
                <div className="cursor-move text-muted-foreground">
                  <GripVertical className="h-5 w-5" />
                </div>

                {member.photoUrl ? (
                  <img
                    src={member.photoUrl}
                    alt={member.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                    <Users className="h-8 w-8 text-muted-foreground" />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold">{member.name}</h3>
                    <Badge variant={ROLE_COLORS[member.role] || "default"}>
                      {ROLE_LABELS[member.role] || member.role}
                    </Badge>
                    {!member.isActive && (
                      <Badge variant="outline">Inactivo</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {member.title}
                  </p>
                  <p className="text-sm text-muted-foreground line-clamp-1 mt-1">
                    {member.bio}
                  </p>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleActiveMutation.mutate(member.id)}
                    disabled={toggleActiveMutation.isPending}
                    title={member.isActive ? "Desactivar" : "Activar"}
                  >
                    {member.isActive ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(member)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeleteDialog({ isOpen: true, member })}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <TeamMemberModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        member={editingMember}
      />

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, member: null })}
        onConfirm={handleConfirmDelete}
        title="Eliminar Miembro"
        description={`¿Estás seguro de eliminar a "${deleteDialog.member?.name}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
